import { useCallback, useEffect } from 'react';
import { LocalStorage, getLocalStorageItem, getTokenData, removeTokenData } from '../utils/localStorageService';
import { useCookies } from 'react-cookie';
import { useUser } from '../features/user/useUser';
import { AppRoutes } from '../app/routes';
import { userLoadingSignal, userLoggedInSignal, userMessageSignal, userUUIDSignal } from '../features/user/userSignals';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';
import { deleteAllCookies, useRoleDetector, removeCookieWithDomain } from '../utils/userUtil';
import { useAtom } from 'jotai';
import { preLoginUser } from '../app/store';
import { init } from '@noriginmedia/norigin-spatial-navigation';
import { startExtensionMutationObserver } from '../features/detectors/ExtensionDetector';
import { setupInteractionListeners } from '../features/utils/interactionTracker';

init({
  // options
  debug: false,
  visualDebug: false,
});

export const useAppInit = () => {
  const { roleDetector } = useRoleDetector();
  const [uuidCookie, setUuidCookie] = useCookies(['cpId']);
  const [, setCookie] = useCookies(['clearplay_token', 'cp_isLoggedIn']);
  const { fetchUserFilteringStatus } = useUser();
  const { useMe } = useUser();
  const { refetch: fetchMe } = useMe();
  const [, setPreLoginCodes] = useAtom(preLoginUser);

  const setUUID = useCallback(async () => {
    if (uuidCookie.cpId) {
      userUUIDSignal.value = uuidCookie.cpId;
    }
    if (!uuidCookie.cpId) {
      const id = uniqueId('cpId_');
      setUuidCookie('cpId', id, {
        expires: dayjs().add(7, 'days').toDate(),
      });
    }
  }, [uuidCookie, setUuidCookie]);

  const init = useCallback(async () => {
    await setUUID();
    await fetchUserFilteringStatus();
  }, [setUUID, fetchUserFilteringStatus]);

  useEffect(() => {
    const fetchUserIfToken = async () => {
      const token = await getTokenData();
      if (token) {
        await fetchMe();
      }
    };
    fetchUserIfToken();
    // The empty dependency array ensures this runs only once.
  }, [fetchMe]);

  const handleUserAuthentication = useCallback(async () => {
    const token = await getTokenData();
    if (token && window.location.pathname !== AppRoutes.SIGNIN_CALLBACK) {
      userLoggedInSignal.value = true;
      userLoadingSignal.value = false;
      try {
        roleDetector(token);

        if (window.location.pathname === AppRoutes.SIGNUP_EMAIL || window.location.pathname === AppRoutes.ENTER_EMAIL)
          return;
        setCookie('cp_isLoggedIn', 'true', { domain: process.env.REACT_APP_DOMAIN });
        setCookie('clearplay_token', `${token}`, {
          expires: dayjs().add(6, 'months').toDate(),
          domain: process.env.REACT_APP_DOMAIN,
        });
        if (window.location.pathname === AppRoutes.ENTER_EMAIL) {
          window.location.pathname = AppRoutes.MY_ACCOUNT;
        }
      } catch (err) {
        removeTokenData();
        deleteAllCookies();
        userMessageSignal.value = {
          message: 'You have been logged out, please log in again.',
          color: 'red',
        };
        if (window.location.pathname !== AppRoutes.ENTER_EMAIL) {
          window.location.pathname = AppRoutes.ENTER_EMAIL;
        }
      }
    }
  }, [roleDetector, setCookie]);

  useEffect(() => {
    startExtensionMutationObserver();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const redirect = getLocalStorageItem(LocalStorage.REDIRECT_URL);
    const promo = getLocalStorageItem(LocalStorage.PROMO_CODE);
    const referral = getLocalStorageItem(LocalStorage.REFERRAL_SOURCE);

    setPreLoginCodes((prev) => ({
      ...prev,
      referral: referral ? referral : '',
      promo: promo ? promo : '',
      redirect: redirect ? encodeURIComponent(redirect) : '',
    }));
  }, [setPreLoginCodes]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    const badCookieName = 'clearplay_token';
    const badCookieValueStartsWith = '%7B%22domain';
    const badCookiePresent = document.cookie.split('; ').find((cookie) => {
      let [name, value] = cookie.split('=');
      return name === badCookieName && value.startsWith(badCookieValueStartsWith);
    });

    if (badCookiePresent) {
      removeCookieWithDomain(badCookieName, process.env.REACT_APP_DOMAIN || '.clearplay.com');
      console.log(`Old cookie '${badCookieName}' removed`);
    }
  }, []);

  useEffect(() => {
    handleUserAuthentication();
  }, [handleUserAuthentication]);

  useEffect(() => {
    setupInteractionListeners();
  }, []);
};
